import { _ as _slicedToArray } from '../../unsorted/slicedToArray.js';
import { motion } from 'framer-motion';
import { d as debounce } from '../../unsorted/index5.js';
import { useContext, useRef, useState, useMemo, useCallback, useEffect } from 'react';
import { m as merge } from '../../unsorted/index.js';
import { a as DEFAULT_BODY_COLOR } from '../../components/shared/shared3.js';
import { r as recursivelyAddTooltipAttrsToChildNodes } from '../../components/shared/shared2.js';
import { TOOLTIP_SIZES, DEFAULT_TOOLTIP_SIZE } from '../../constants.js';
import { u as useTooltipStore } from '../../providers/tooltipContext/tooltipContext.js';
import { isTouchDevice } from '../../utils/deviceHelpers.js';
import { getSafeCenterHorizontalPosition, getSafeTopVerticalPosition } from '../../utils/positionHelpers.js';
import { getResponsiveSx, getStartingSize } from '../../utils/styleHelpers.js';
import { getBodyTextStyles } from '../../utils/textStyleHelpers.js';
import { _ as _defineProperty } from '../../unsorted/defineProperty.js';
import { jsxs, Fragment, jsx } from '@emotion/react/jsx-runtime';
import '@emotion/react';
import { B as BiomeShadowRootContext } from '../../providers/shadowRootContext/shadowRootContext.js';
import { useConvertSxToEmotionStyles } from '../../hooks/useConvertSxToEmotionStyles.js';
import { useTheme } from '../../hooks/useTheme.js';
import { useEventListener } from '../../hooks/useEventListener.js';

function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var baseContainerSx = {
  position: "absolute",
  maxw: "370px",
  brad: "base.borderRadius.x4",
  boxShadow: "base.shadow.500",
  background: "base.color.neutral.500",
  textAlign: "center"
};
function getContainerStyles(_ref) {
  var size = _ref.size,
    theme = _ref.theme;
  switch (size) {
    case "small":
      return {
        padding: theme.base.spacing.x3
      };
    case "large":
      return {
        padding: theme.base.spacing.x5
      };
    case "medium":
    default:
      return {
        padding: theme.base.spacing.x4
      };
  }
}
var responsiveContainerStyles = function responsiveContainerStyles(props) {
  return getResponsiveSx(_objectSpread(_objectSpread({}, props), {}, {
    renderSxAtSize: getContainerStyles
  }));
};

var getBoundingRect = function getBoundingRect(element) {
  return element.getBoundingClientRect();
};
var LEAVE_DELAY = 55;
function TooltipOverlay() {
  var shadowRoot = useContext(BiomeShadowRootContext);
  var containerElementRef = useRef(shadowRoot || window);
  var theme = useTheme();
  var baseContainerStyles = useConvertSxToEmotionStyles(merge({
    c: DEFAULT_BODY_COLOR
  }, baseContainerSx));
  var hoverLeaveTimer = useRef();
  var currentTooltipBubbleRef = useRef(null);
  var animatedTooltipBubbleRef = useRef(null);
  var _useTooltipStore = useTooltipStore(function (state) {
      return state.tooltipList;
    }),
    tooltipList = _useTooltipStore.state;
  var _useState = useState(undefined),
    _useState2 = _slicedToArray(_useState, 2),
    currentTooltip = _useState2[0],
    setCurrentTooltip = _useState2[1];
  var _useState3 = useState(undefined),
    _useState4 = _slicedToArray(_useState3, 2),
    currentTooltipTargetRect = _useState4[0],
    setCurrentTooltipTargetRect = _useState4[1];
  var _useState5 = useState(undefined),
    _useState6 = _slicedToArray(_useState5, 2),
    currentTooltipBubbleRect = _useState6[0],
    setCurrentTooltipBubbleRect = _useState6[1];
  var calculatedPosition = useMemo(function () {
    if (!currentTooltipTargetRect || !currentTooltipBubbleRect || !currentTooltip) {
      return {
        left: 0,
        top: 0
      };
    }
    var left = getSafeCenterHorizontalPosition(currentTooltipTargetRect, currentTooltipBubbleRect);
    var top = getSafeTopVerticalPosition(currentTooltipTargetRect, currentTooltipBubbleRect, 16);
    return {
      left: left,
      top: top
    };
  }, [currentTooltip, currentTooltipBubbleRect, currentTooltipTargetRect]);
  var onDocumentBodyClick = useCallback(function (event) {
    if (isTouchDevice() && tooltipList.length > 0) {
      var clickedElement = event.target;
      var clickedTooptiptId = clickedElement.getAttribute("data-tooltip-id");
      var isTooltipTarget = clickedElement.getAttribute("data-tooltip-target") === "true";
      var clickingOnACurrentlyOpenTooltip = clickedTooptiptId === (currentTooltip === null || currentTooltip === void 0 ? void 0 : currentTooltip.id);
      if (!isTooltipTarget && clickingOnACurrentlyOpenTooltip) {
        // @NOTE: If we're clicking on a tooltip bubble element, then do nothing
        // (allows user to select things inside of a tooltip)
        return false;
      }
      if (isTooltipTarget && clickingOnACurrentlyOpenTooltip) {
        // @NOTE: If the target is clicked again and the tooltip is already open, toggle it closed
        setCurrentTooltip(undefined);
      } else if (clickedTooptiptId !== (currentTooltip === null || currentTooltip === void 0 ? void 0 : currentTooltip.id)) {
        // @NOTE: Else If the tooltip id of the clicked element is different to the
        // current tooltip, open a new one
        setCurrentTooltip(tooltipList.find(function (tooltip) {
          return tooltip.id === clickedTooptiptId;
        }));
      } else {
        // @NOTE: Else we're not clicking atop a tooltip, so just close any open tooltip
        setCurrentTooltip(undefined);
      }
      return false;
    }
    return false;
  }, [tooltipList, currentTooltip]);
  var clearCurrentTooltipData = useCallback(function () {
    setCurrentTooltip(undefined);
    setCurrentTooltipTargetRect(undefined);
    setCurrentTooltipBubbleRect(undefined);
  }, []);
  var handleHoverStart = useCallback(function (hoveredTooptipTargetId) {
    clearTimeout(hoverLeaveTimer.current);
    if (hoveredTooptipTargetId !== (currentTooltip === null || currentTooltip === void 0 ? void 0 : currentTooltip.id)) {
      var newTooltip = tooltipList.find(function (tooltip) {
        return tooltip.id === hoveredTooptipTargetId;
      });
      setCurrentTooltip(newTooltip);
    }
  }, [tooltipList, currentTooltip]);
  var handleHoverTooltipClose = useCallback(function () {
    clearTimeout(hoverLeaveTimer.current);
    if (currentTooltip) {
      clearCurrentTooltipData();
    }
  }, [currentTooltip, clearCurrentTooltipData]);
  var handleHoverStop = useCallback(function () {
    clearTimeout(hoverLeaveTimer.current);
    hoverLeaveTimer.current = window.setTimeout(function () {
      handleHoverTooltipClose();
    }, LEAVE_DELAY);
  }, [handleHoverTooltipClose]);
  var onMouseMove = useCallback(function (event) {
    if (!isTouchDevice() && tooltipList.length > 0) {
      var hoveredElement = event.target;
      var hoveredTooptipTargetId = hoveredElement === null || hoveredElement === void 0 ? void 0 : hoveredElement.getAttribute("data-tooltip-id");
      if (hoveredTooptipTargetId) {
        handleHoverStart(hoveredTooptipTargetId);
      } else {
        handleHoverStop();
      }
      return false;
    }
    return false;
  }, [tooltipList.length, handleHoverStart, handleHoverStop]);
  useEventListener("mousemove", onMouseMove, containerElementRef);
  useEventListener("click", onDocumentBodyClick, containerElementRef);
  var onResizeAndScroll = useCallback(function () {
    if (currentTooltip) {
      clearCurrentTooltipData();
    }
  }, [currentTooltip, clearCurrentTooltipData]);
  var debouncedOnResizeAndOnScroll = debounce(onResizeAndScroll, 300, {
    leading: true
  });
  useEventListener("scroll", debouncedOnResizeAndOnScroll, containerElementRef, true);
  useEventListener("resize", debouncedOnResizeAndOnScroll, containerElementRef);
  useEffect(function () {
    if (shadowRoot) {
      containerElementRef.current = shadowRoot;
    }
  }, [shadowRoot]);
  useEffect(function () {
    if (currentTooltip) {
      var target = shadowRoot ? shadowRoot.querySelector("[data-tooltip-id=\"".concat(currentTooltip.id, "\"]")) : document.body.querySelector("[data-tooltip-id=\"".concat(currentTooltip.id, "\"]"));
      if (target) {
        setCurrentTooltipTargetRect(getBoundingRect(target));
      } else {
        setCurrentTooltipTargetRect(undefined);
      }
      recursivelyAddTooltipAttrsToChildNodes(animatedTooltipBubbleRef.current, currentTooltip.id);
    } else {
      setCurrentTooltipTargetRect(undefined);
    }
  }, [currentTooltip, shadowRoot]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(function () {
    if (!currentTooltipBubbleRef.current) return;
    setCurrentTooltipBubbleRect(getBoundingRect(currentTooltipBubbleRef.current));
  }, [currentTooltipTargetRect, currentTooltipBubbleRef]);

  // @NOTE: if the tooltipList changes to be empty, then we should
  // clear out the current tooltip (if there is one)
  useEffect(function () {
    if (tooltipList.length === 0 && currentTooltip) {
      clearCurrentTooltipData();
    }
  }, [tooltipList, currentTooltip, clearCurrentTooltipData]);

  // @NOTE: if there is no current tooltips open, simply return null
  if (!currentTooltip) return null;
  var startingSize = getStartingSize(currentTooltip === null || currentTooltip === void 0 ? void 0 : currentTooltip.size, DEFAULT_TOOLTIP_SIZE, TOOLTIP_SIZES);
  var allContainerStyles = merge(baseContainerStyles, getBodyTextStyles({
    size: "small",
    mono: false,
    themeProps: theme,
    weight: "regular"
  }), getContainerStyles({
    size: startingSize,
    theme: theme
  }), responsiveContainerStyles({
    size: currentTooltip === null || currentTooltip === void 0 ? void 0 : currentTooltip.size,
    theme: theme
  }), {
    top: calculatedPosition.top,
    left: calculatedPosition.left
  });
  return jsxs(Fragment, {
    children: [jsx("div", {
      ref: currentTooltipBubbleRef,
      css: merge(allContainerStyles, {
        top: "0",
        left: "0",
        opacity: 0,
        pointerEvents: "none"
      }),
      children: currentTooltip.content
    }), jsx(motion.div, {
      ref: animatedTooltipBubbleRef,
      "data-tooltip-id": currentTooltip.id,
      "data-testid": "tooltip__container",
      initial: {
        opacity: 0,
        y: "-8%"
      },
      animate: {
        opacity: 1,
        y: "0%"
      },
      transition: {
        duration: theme.base.motion.normal.fast.jsDuration,
        ease: theme.base.motion.normal.fast.jsEase
      },
      css: allContainerStyles,
      children: currentTooltip.content
    }, currentTooltip.id)]
  });
}

export { TooltipOverlay as T };
//# sourceMappingURL=TooltipOverlay.js.map
